// utils/fetchErrorHandler.js

/**
 * Maneja errores de fetch y devuelve un mensaje amigable en español
 * @param {Error} error - El error capturado
 * @param {string} defaultMessage - Mensaje por defecto si no se puede determinar el error
 * @returns {string} Mensaje de error amigable
 */
export const getFriendlyErrorMessage = (error, defaultMessage = 'Ocurrió un error al procesar la solicitud') => {
  // Si el error es un objeto con mensaje
  if (error?.message) {
    const msg = error.message.toLowerCase();
    
    // Errores de red / conexión
    if (msg.includes('failed to fetch') || msg.includes('network') || msg.includes('fetch')) {
      return 'No se pudo conectar con el servidor. Verificá tu conexión a internet.';
    }
    
    // Errores de timeout
    if (msg.includes('timeout') || msg.includes('aborted')) {
      return 'El servidor tardó demasiado en responder. Intentá nuevamente.';
    }
    
    // Errores de CORS
    if (msg.includes('cors') || msg.includes('blocked')) {
      return 'Error de conexión con el servidor. Contactá con soporte.';
    }
    
    // Si el mensaje viene del backend con formato JSON
    if (error.response?.data?.message) {
      return error.response.data.message;
    }
    
    // Si el mensaje ya está en español, devolverlo
    if (msg.includes('no se pudo') || msg.includes('error al')) {
      return error.message;
    }
  }
  
  // Si el error es un string
  if (typeof error === 'string') {
    return error;
  }
  
  return defaultMessage;
};

/**
 * Envuelve una función fetch con manejo de errores amigables
 * @param {Function} fetchFn - La función fetch a ejecutar
 * @param {Object} options - Opciones de configuración
 * @param {string} options.defaultMessage - Mensaje por defecto en caso de error
 * @param {boolean} options.showError - Si debe mostrar el error automáticamente
 * @param {Function} options.onError - Callback para manejar el error
 * @returns {Promise} La respuesta del fetch
 */
export const safeFetch = async (fetchFn, options = {}) => {
  const { defaultMessage = 'Ocurrió un error al procesar la solicitud', showError = true, onError } = options;
  
  try {
    const response = await fetchFn();
    
    if (!response.ok) {
      let errorMessage = defaultMessage;
      
      try {
        const data = await response.json();
        if (data?.message) {
          errorMessage = data.message;
        }
      } catch {
        // Si no se puede parsear JSON, usar el status
        if (response.status === 404) {
          errorMessage = 'No se encontró el recurso solicitado.';
        } else if (response.status === 400) {
          errorMessage = 'La solicitud es inválida. Verificá los datos ingresados.';
        } else if (response.status === 401) {
          errorMessage = 'No tenés permisos para realizar esta acción.';
        } else if (response.status === 403) {
          errorMessage = 'Acceso denegado. No tenés permisos suficientes.';
        } else if (response.status === 500) {
          errorMessage = 'Error interno del servidor. Intentá nuevamente más tarde.';
        } else {
          errorMessage = `Error ${response.status}: ${response.statusText || 'Error desconocido'}`;
        }
      }
      
      const error = new Error(errorMessage);
      error.status = response.status;
      throw error;
    }
    
    return response;
  } catch (error) {
    const friendlyMessage = getFriendlyErrorMessage(error, defaultMessage);
    error.friendlyMessage = friendlyMessage;
    
    if (showError && typeof window !== 'undefined') {
      // Esto es para que el componente pueda mostrar el error con toast
      // Lo manejamos desde el componente
    }
    
    if (onError) {
      onError(friendlyMessage, error);
    }
    
    throw error;
  }
};

/**
 * Obtiene el mensaje de error de una respuesta HTTP
 * @param {Response} response - La respuesta fetch
 * @param {string} defaultMessage - Mensaje por defecto
 * @returns {Promise<string>} Mensaje de error amigable
 */
export const getErrorMessageFromResponse = async (response, defaultMessage = 'Error al procesar la solicitud') => {
  try {
    const data = await response.json();
    if (data?.message) {
      return data.message;
    }
    if (data?.title) {
      return data.title;
    }
    if (data?.error) {
      return data.error;
    }
  } catch {
    // Si no es JSON, usar el status
  }
  
  switch (response.status) {
    case 400: return 'La solicitud es inválida. Verificá los datos ingresados.';
    case 401: return 'No tenés permisos para realizar esta acción.';
    case 403: return 'Acceso denegado. No tenés permisos suficientes.';
    case 404: return 'No se encontró el recurso solicitado.';
    case 500: return 'Error interno del servidor. Intentá nuevamente más tarde.';
    default: return defaultMessage;
  }
};